import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';

import useEventTracking from '@hooks/useEventTracking';
import { Article } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import { ImpressionElement, impression } from '@tracking';
import {
  MINIMUM_RECOMMENDED_ARTICLES,
  getBlueconicRecommendations,
} from '@utils/blueconic/recommendations';

import StoryItem from './StoryItem';

interface RelatedStoriesProps {
  articles: Article[];
  className?: string;
  gridClassName?: string;
  headline?: string;
  headlineClassName?: string;
  skipBlueConicRecommendations?: boolean;
}

const RelatedStories = ({
  articles,
  className,
  gridClassName,
  headline = 'Recommended',
  headlineClassName,
  skipBlueConicRecommendations = false,
}: RelatedStoriesProps): JSX.Element => {
  const { Track, trackEvent } = useEventTracking({ location: TrackingLocation.RelatedStories });
  const [articleRecommendation, setArticleRecommendation] = useState<Article[]>([]);

  useEffect(() => {
    const fetchBlueconicRecommendations = async () => {
      try {
        const blueconicRecommendations: Article[] = await getBlueconicRecommendations();

        if (blueconicRecommendations.length >= MINIMUM_RECOMMENDED_ARTICLES) {
          setArticleRecommendation(blueconicRecommendations);
        } else {
          setArticleRecommendation(articles);
        }
      } catch (error) {
        console.error('Error fetching recommendations from BlueConic:', error);
      }
    };

    if (!skipBlueConicRecommendations) {
      fetchBlueconicRecommendations();
    } else {
      setArticleRecommendation(articles);
    }
  }, [articles, skipBlueConicRecommendations]);

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: ImpressionElement.RelatedStories }));
        }}
      >
        <aside
          className={clsx('mx-0 border-0 border-t border-solid border-black pt-28', className)}
          data-testid="related-stories"
        >
          {headline && (
            <header>
              <h3 className={clsx('mb-24 font-suisse-works text-headline-md', headlineClassName)}>
                {headline}
              </h3>
            </header>
          )}

          <div
            className={clsx(
              'grid grid-cols-1 gap-36 xs:gap-48 sm:grid-cols-2 md:grid-cols-4',
              gridClassName
            )}
          >
            {articleRecommendation.length > 0 &&
              articleRecommendation.map((article) => <StoryItem key={article.id} {...article} />)}
          </div>
        </aside>
      </InView>
    </Track>
  );
};

export default RelatedStories;
